/**
 * Lectura de `paleta.css` + `semantico.css` como TEXTO, para resolver un rol a
 * su color concreto en un modo dado.
 *
 * Para que existe: MUI manipula algunas entradas de su paleta con `alpha()`,
 * `darken()` o `emphasize()` (hover de IconButton, seleccion de ToggleButton,
 * Snackbar, Alert...). Esas funciones truenan con `var(--x)` y tampoco
 * entienden `oklch()`. En lugar de copiar valores a TypeScript —dos fuentes que
 * acabarian discrepando—, se leen las hojas reales y se traducen a sRGB.
 *
 * Las hojas siguen siendo la UNICA fuente de verdad: cambiar un valor en
 * `paleta.css` cambia lo que pinta el CSS y lo que recibe MUI a la vez.
 */
import paletaCss from './paleta.css?raw';
import semanticoCss from './semantico.css?raw';
import { formatoMui, leerColor, type Rgb } from './color';

export type ModoResuelto = 'claro' | 'oscuro';

/** Variables declaradas por bloque: el de `:root` y el de `[data-tema='oscuro']`. */
interface Declaraciones {
  claro: Map<string, string>;
  oscuro: Map<string, string>;
}

/** Quita comentarios y los bloques `@media` (densidad coarse, movimiento reducido). */
function sinMediaNiComentarios(css: string): string {
  let texto = css.replace(/\/\*[\s\S]*?\*\//g, '');
  let inicio = texto.indexOf('@media');
  while (inicio >= 0) {
    const abre = texto.indexOf('{', inicio);
    if (abre < 0) break;
    let nivel = 0;
    let fin = abre;
    for (; fin < texto.length; fin++) {
      if (texto[fin] === '{') nivel++;
      else if (texto[fin] === '}' && --nivel === 0) break;
    }
    texto = texto.slice(0, inicio) + texto.slice(fin + 1);
    inicio = texto.indexOf('@media');
  }
  return texto;
}

export function leerDeclaraciones(...hojas: string[]): Declaraciones {
  const resultado: Declaraciones = { claro: new Map(), oscuro: new Map() };
  for (const hoja of hojas) {
    const bloques = sinMediaNiComentarios(hoja).matchAll(/([^{}]+)\{([^{}]*)\}/g);
    for (const [, selectorCrudo, cuerpo] of bloques) {
      const selector = (selectorCrudo ?? '').trim().replace(/"/g, "'");
      const destino =
        selector === ':root'
          ? resultado.claro
          : selector === ":root[data-tema='oscuro']"
            ? resultado.oscuro
            : null;
      if (!destino) continue;
      for (const declaracion of (cuerpo ?? '').split(';')) {
        const dos = declaracion.indexOf(':');
        if (dos < 0) continue;
        const nombre = declaracion.slice(0, dos).trim();
        if (!nombre.startsWith('--')) continue;
        destino.set(
          nombre,
          declaracion
            .slice(dos + 1)
            .trim()
            .replace(/\s+/g, ' '),
        );
      }
    }
  }
  return resultado;
}

const DECLARACIONES = leerDeclaraciones(paletaCss, semanticoCss);

/** Variables extra que no viven en las hojas (el acento calculado de la marca). */
export type Extra = Partial<Record<ModoResuelto, Record<string, string>>>;

/**
 * Resuelve `--rol` a su valor final en el modo pedido, siguiendo `var()`.
 * El oscuro hereda del claro lo que no redefine, igual que la cascada.
 */
export function resolverValor(
  nombre: string,
  modo: ModoResuelto,
  extra: Extra = {},
  declaraciones: Declaraciones = DECLARACIONES,
  profundidad = 0,
): string | null {
  if (profundidad > 20) return null;
  const valor =
    (modo === 'oscuro'
      ? (extra.oscuro?.[nombre] ?? declaraciones.oscuro.get(nombre))
      : undefined) ??
    extra.claro?.[nombre] ??
    declaraciones.claro.get(nombre);
  if (valor === undefined) return null;
  let fallo = false;
  const expandido = valor.replace(
    /var\(\s*(--[\w-]+)\s*(?:,\s*([^)]*))?\)/g,
    (_, ref, respaldo) => {
      const r = resolverValor(ref, modo, extra, declaraciones, profundidad + 1) ?? respaldo ?? null;
      if (r === null) fallo = true;
      return r ?? '';
    },
  );
  return fallo ? null : expandido;
}

/** Color concreto (sRGB) de un rol. Lanza si el rol no existe o no es un color. */
export function colorDeRol(nombre: string, modo: ModoResuelto, extra: Extra = {}): Rgb {
  const valor = resolverValor(nombre, modo, extra);
  const color = valor === null ? null : leerColor(valor);
  if (!color) throw new Error(`El rol ${nombre} no se resuelve a un color (${valor ?? 'ausente'})`);
  return color;
}

/** El mismo color en la forma que MUI sabe manipular (`#rrggbb` o `rgba()`). */
export function colorMui(nombre: string, modo: ModoResuelto, extra: Extra = {}): string {
  return formatoMui(colorDeRol(nombre, modo, extra));
}
